import {
	ActionBar,
	SpinnerWithBackground,
	Text,
	TextArea,
	Toggle,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useEnpsStore } from '../store'
import type { EnpsConfigurationTabInput } from './types'

/**
 * @fileoverview eNPS Configuration Tab
 *
 * Customize survey questions
 */

/**
 * Scale preview component showing NPS categories
 */
const NpsScalePreview = () => {
	const categories = [
		{ range: '0-6', label: 'Detractors', color: 'bg-red-100 border-red-300 text-red-800' },
		{ range: '7-8', label: 'Passives', color: 'bg-yellow-100 border-yellow-300 text-yellow-800' },
		{ range: '9-10', label: 'Promoters', color: 'bg-green-100 border-green-300 text-green-800' },
	]

	return (
		<div className='bg-gray-50 border border-gray-200 rounded-lg p-4'>
			<Text variant='small' weight='semibold' className='text-gray-700 mb-3'>
				NPS Scale Preview
			</Text>
			<div className='flex gap-2 mb-4'>
				{Array.from({ length: 11 }, (_, i) => {
					let bgColor = 'bg-red-100 text-red-800'
					if (i >= 9) bgColor = 'bg-green-100 text-green-800'
					else if (i >= 7) bgColor = 'bg-yellow-100 text-yellow-800'
					return (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: fixed 0-10 NPS scale, index IS the score value
							key={`score-${i}`}
							className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium ${bgColor}`}>
							{i}
						</div>
					)
				})}
			</div>
			<div className='flex gap-4'>
				{categories.map((cat) => (
					<div key={cat.label} className={`flex-1 px-3 py-2 rounded border ${cat.color}`}>
						<Text variant='xsmall' weight='semibold'>
							{cat.label}
						</Text>
						<Text variant='xsmall' className='opacity-75'>
							Score: {cat.range}
						</Text>
					</div>
				))}
			</div>
			<Text variant='xsmall' className='text-gray-500 mt-3'>
				NPS = % Promoters - % Detractors (Range: -100 to +100)
			</Text>
		</div>
	)
}

export const EnpsConfigurationTab = () => {
	const { organizationId } = useParams<{
		organizationId: string
		enpsId: string
	}>()
	const navigate = useNavigate()
	const { enpsConfig, useEditEnpsConfig } = useEnpsStore()
	const { editEnpsConfig, isLoading } = useEditEnpsConfig(enpsConfig?.id)

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty },
		reset,
	} = useForm<EnpsConfigurationTabInput>({
		defaultValues: {
			questionText: enpsConfig?.questionText || '',
			followUpQuestionText: enpsConfig?.followUpQuestionText || '',
			textFeedbackEnabled: enpsConfig?.textFeedbackEnabled ?? true,
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	const textFeedbackEnabled = useWatch({ control, name: 'textFeedbackEnabled' })

	useEffect(() => {
		if (enpsConfig) {
			reset({
				questionText: enpsConfig.questionText,
				followUpQuestionText: enpsConfig.followUpQuestionText,
				textFeedbackEnabled: enpsConfig.textFeedbackEnabled,
			})
		}
	}, [enpsConfig, reset])

	const handleSubmitInternal = async (data: EnpsConfigurationTabInput) => {
		await editEnpsConfig(data)
		reset(data)
	}

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-24 mt-6 w-full'>
			{isLoading && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 gap-6 px-6 max-w-4xl'>
				<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
					<Text variant='small' weight='semibold' className='text-blue-900'>
						NPS Survey Format
					</Text>
					<Text variant='xsmall' className='text-blue-700 mt-2'>
						Participants will be asked to rate their likelihood on a scale of 0-10, followed by an optional feedback
						question. The NPS score is calculated as: % Promoters (9-10) - % Detractors (0-6).
					</Text>
				</div>

				<NpsScalePreview />

				<Controller
					name='questionText'
					control={control}
					rules={{
						required: 'Main question is required',
						minLength: { value: 10, message: 'Question should be at least 10 characters' },
					}}
					render={({ field }) => (
						<TextArea
							label='Main Question'
							placeholder='How likely are you to recommend our organization as a place to work?'
							variant={errors.questionText ? 'error' : 'default'}
							hint={errors.questionText?.message as string}
							value={field.value}
							onChange={field.onChange}
							onBlur={field.onBlur}
							rows={3}
							required
						/>
					)}
				/>

				<div className='border border-gray-200 rounded-lg p-4'>
					<div className='flex items-center justify-between mb-4'>
						<div>
							<Text variant='small' weight='semibold' className='text-gray-900'>
								Enable Follow-up Question
							</Text>
							<Text variant='xsmall' className='text-gray-500 mt-1'>
								Allow participants to provide additional feedback after rating
							</Text>
						</div>
						<Controller
							name='textFeedbackEnabled'
							control={control}
							render={({ field }) => <Toggle checked={field.value} onChange={field.onChange} size='small' />}
						/>
					</div>

					{textFeedbackEnabled && (
						<Controller
							name='followUpQuestionText'
							control={control}
							rules={{
								required: textFeedbackEnabled ? 'Follow-up question is required' : false,
								minLength: { value: 5, message: 'Question should be at least 5 characters' },
							}}
							render={({ field }) => (
								<TextArea
									label='Follow-up Question'
									placeholder='What is the primary reason for your score?'
									variant={errors.followUpQuestionText ? 'error' : 'default'}
									hint={errors.followUpQuestionText?.message as string}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									rows={2}
									required
								/>
							)}
						/>
					)}
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate(`/organizations/${organizationId}/enps`),
					},
					{
						variant: 'primary' as const,
						label: 'Save',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
